// LiveMarkdown.tsx — render one tool's UI.
//
// The contract this file exists to keep: *components are not replaced
// unless they have to be*. A tool may rewrite its markdown at any time,
// and a `<terminal>` that has accumulated ten thousand lines must come
// through that rewrite with its socket open and its scrollback intact.
//
// Two mechanisms, because React's reconciler alone is not enough:
//
//   1. Buffers and sockets live in channelStore, outside React entirely,
//      so even a genuine remount is lossless.
//   2. The node list is FLAT and keyed by component id. React reconciles
//      siblings by key, so prose changing above or below a component
//      leaves it mounted. Flattening is what makes that reliable —
//      nesting a component inside prose would change its parent identity
//      whenever the prose reflowed, forcing the remount that mechanism 1
//      then has to paper over.
//
// Eviction is deferred rather than immediate for the same reason: a
// replacement that re-declares the same component arrives as an unmount
// followed instantly by a mount, and closing the socket in between would
// throw away exactly what we are trying to preserve.

import React, { useEffect, useRef } from 'react';
import { Markdown } from 'common';
import { parseToolUI } from './parse';
import { RENDERERS } from './components';
import { evictUnreferenced } from './channelStore';

// Grace between a component disappearing and its socket closing. Mirrors
// EVICTION_GRACE_SECONDS in app/harness/channels.py — the two ends should
// give up at roughly the same time.
const EVICT_GRACE_MS = 30_000;

export interface LiveMarkdownProps {
    markdown: string;
    sessionId: string;
    toolCallId: string;
}

export const LiveMarkdown: React.FC<LiveMarkdownProps> = ({
    markdown,
    sessionId,
    toolCallId,
}) => {
    const { nodes } = React.useMemo(() => parseToolUI(markdown), [markdown]);
    const evictTimer = useRef<number | null>(null);

    // After the DOM settles on a new UI, close the sockets of components
    // that did not come back. Anything still rendered has a live ref
    // count and is left alone.
    useEffect(() => {
        const live = new Set(
            nodes.filter((n) => n.kind === 'component').map((n) => (n as { id: string }).id),
        );
        if (evictTimer.current !== null) window.clearTimeout(evictTimer.current);
        evictTimer.current = window.setTimeout(() => {
            evictUnreferenced(live);
        }, EVICT_GRACE_MS);
        return () => {
            if (evictTimer.current !== null) window.clearTimeout(evictTimer.current);
        };
    }, [nodes]);

    if (!nodes.length) return null;

    return (
        <div className="markdown-content">
            {nodes.map((node, index) => {
                if (node.kind === 'prose') {
                    // Prose is keyed positionally on purpose. It carries no
                    // state, so a churning key costs nothing — and giving
                    // components their own stable keys is what matters.
                    return <Markdown key={`prose-${index}`}>{node.markdown}</Markdown>;
                }
                const Renderer = RENDERERS[node.tag];
                if (!Renderer) return null;
                return (
                    <Renderer
                        key={node.id}
                        node={node}
                        sessionId={sessionId}
                        toolCallId={toolCallId}
                    />
                );
            })}
        </div>
    );
};

export default LiveMarkdown;
